import Errored from '../core/Errored';
import SecondaryButton from '../core/SecondaryButton';
import PrimaryAddButton from '../core/PrimaryAddButton';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../core/AddButton';
import CancelButton from '../core/CancelButton';
import PrimaryButton from '../core/PrimaryButton';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import SearchSelect from '../core/SearchSelect';
import { globalStyles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { checkValidForDatatype, computeConceptIcon, getResourceLabel, makeCancelable } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import { ALIAS_LABELS } from './constants';
import ComponentLinkList from './ontologyproject/ComponentLinkList';

const styles = Object.assign({}, globalStyles);

class FindResourceDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: props.open,
            wrongType: false,
            offerExternal: false,
            lang: props.viewLang,
            externalDuplicate: false,
            isErrored: undefined
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
        return this.cancellable;
    }

    changeLanguage = (lang) => this.setState({ lang });

    handleClickOpen = () => this.setState({ open: true, externalDuplicate: false, offerExternal: false });

    computeSearch = (value, lang) => {
        let excludeByPath = this.props.excludeByPath && this.props.excludeByPath.length > 0 ?
            this.props.excludeByPath
            :
            this.props.excludeByPathList ?
                this.props.excludeByPathList.map(exclusion => {
                    return {
                        predicate: exclusion,
                        target: this.props.source
                    };
                })
                :
                null;
        let graphList = [this.props.graph];
        // We include the system graph for user for appropriate properties to allow things like XL Label class to be found
        if (this.props.includeSystemGraph) graphList.push(GS.getActiveSubscription());
        let exludeList = [
            "http://www.w3.org/2002/07/owl#Thing",
            "http://www.w3.org/2002/07/owl#Nothing",
            "http://www.w3.org/2002/07/owl#topObjectProperty",
            "http://www.w3.org/2002/07/owl#topDataProperty",
            ...this.props.exclude
        ];
        if (this.props.source) exludeList.push(this.props.source);
        let search = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: Array.isArray(this.props.searchClass) ? this.props.searchClass : [this.props.searchClass]
            },
            {
                predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                ...(value !== "") && {
                    value: value,
                    isExactMatch: false
                }
            }
        ];
        if (this.props.searchExcludeClass && this.props.searchExcludeClass.length > 0) {
            search.push({
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: this.props.searchExcludeClass,
                invert: true
            });
        }
        let fullSearch =
        {
            search,
            graph: graphList,
            ...(excludeByPath) && { excludeByPath },
            excludeSystemNamespaces: true,
            exclude: [...new Set(exludeList)],
            language: lang,
            ignoreNoLanguage: lang !== "und",
            fast: value === "",
            basicSort: value === "",
            pageSize: 20,
            predicates: [
                "http://www.w3.org/2000/01/rdf-schema#label",
                "http://www.w3.org/2002/07/owl#deprecated",
                "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                "https://grafsync.graphifi.com/schema/gs/status",
                "https://grafsync.graphifi.com/schema/gs/visibility"
            ]
        };
        return fullSearch;
    };

    addResource = (id) => {
        this.props.onAddResource(id);
        if (!this.props.allowMultiple) this.exitFindResource(false);
    };

    exitFindResource = (exit = true) => {
        this.setState({
            open: false,
            wrongType: false,
            lang: this.props.viewLang,
            externalDuplicate: false,
            offerExternal: false
        });
        if (exit && this.props.onExitFindResource) this.props.onExitFindResource();
    };

    addExternal = () => this.addResource(this.state.externalIRI);

    declineExternal = () => this.setState({ text: "", offerExternal: false, externalDuplicate: false });

    offerExternal = async (iri, valid = true) => {
        if (!valid
            || iri.includes("graphifi")
            || iri.startsWith("http://www.w3.org/2002/07/owl#")
            || iri.startsWith("http://www.w3.org/1999/02/22-rdf-syntax-ns#")
            || iri.startsWith("http://www.w3.org/2000/01/rdf-schema#")
            || iri.startsWith("http://www.w3.org/2004/02/skos/core#")
            || (this.props.components && this.props.components.includes(iri))) {
            this.setState({
                externalDuplicate: false,
                offerExternal: false,
                wrongType: false
            });
        }
        else if (this.props.exclude.includes(iri) || iri === this.props.source) {
            this.setState({
                externalDuplicate: true,
                offerExternal: false,
                wrongType: false
            });
        }
        else {
            const isValid = checkValidForDatatype(iri, "http://www.w3.org/2001/XMLSchema#anyURI");
            if (isValid && this.props.permittedExternalType) {
                try {
                    GS.handle("find-resource");
                    await this.loadData([iri]).promise;
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    const isLoaded = GS.isLoaded(iri, this.props.graph);
                    if (isLoaded) {
                        const iriData = GS.data(iri, this.props.graph);
                        const datatypeList = Array.isArray(iriData.type) ? iriData.type : [iriData.type];
                        if (!datatypeList.some(dt => this.props.permittedExternalType.includes(dt))) {
                            this.setState({
                                externalDuplicate: false,
                                offerExternal: false,
                                externalIRI: iri,
                                wrongType: true
                            });
                            return;
                        }
                    }
                }
                catch (e) {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                }
            }
            this.setState({
                externalDuplicate: false,
                offerExternal: isValid,
                externalIRI: iri,
                wrongType: false
            });
        }
    };

    render() {

        const { classes, title, sourceComponent, icon, viewLang, exclude, allowMultiple, searchClass, useWorkflow,
            dataTest, sortByLength, permitExternal, useButton, buttonTitle, hideButtonOnOpen } = this.props;
        const { offerExternal, externalDuplicate, lang, wrongType, isErrored, open } = this.state;

        if (isErrored) return <Errored dataTest="fr-error" data={isErrored} />;

        // No need to display if only searching one class of resources
        const displayAlias = Array.isArray(searchClass);

        if (!open) {
            return useButton ?
                <SecondaryButton dataTest="button-choose" onClick={() => this.handleClickOpen()}>{buttonTitle}</SecondaryButton>
                :
                <AddButton icon={icon} dataTest={dataTest} onClick={() => this.handleClickOpen()} title={buttonTitle} />;
        }

        return (
            <React.Fragment>
                {!hideButtonOnOpen &&
                    <AddButton icon={icon} dataTest={dataTest} title={buttonTitle} />
                }
                <Dialog
                    data-test="add-resource-dialog"
                    className={classes.dialog}
                    open={this.state.open}
                    maxWidth="lg"
                    classes={{
                        paper: classes.paper,
                        paperWidthLg: classes.largeDialog
                    }}
                    onClose={this.exitFindResource}
                    aria-labelledby="find-resource-dialog-title">
                    <DialogTitle data-test="find-resource-dialog-title" disableTypography className={classes.mainHeading} id="find-resource-dialog-title">{title || " Resources"}</DialogTitle>
                    <DialogContent style={{ height: 450, width: 600 }}>
                        <div style={{ height: 350 }}>
                            <TitledPanel title="Language">
                                <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={this.changeLanguage} />
                            </TitledPanel>
                            <SearchSelect
                                key={lang}
                                dataTest="resource-label"
                                focus={true}
                                maxLength={250}
                                sortByLength={sortByLength}
                                wrapped={true}
                                search={(value, lang) => this.computeSearch(value, lang)}
                                listSecondIcon={(result) => computeConceptIcon(result, useWorkflow)}
                                listSelectionDisplay={(result, lang) => {
                                    const label = getResourceLabel(result, "label", lang);
                                    return label;
                                }}
                                listDisplay={(result, lang) => {
                                    const label = getResourceLabel(result, "label", lang);
                                    const resultTypes = Array.isArray(result.type) ? result.type : [result.type];
                                    const aliasLabel = resultTypes.filter(resultType => resultType in ALIAS_LABELS)
                                        .map(rt => ALIAS_LABELS[rt])
                                        .sort()
                                        .join(" AND ");
                                    return <div>
                                        <Typography variant="body2">
                                            <Tooltip title={result.id}>
                                                <span data-test="result-text-words">{label}</span>
                                            </Tooltip>
                                        </Typography>
                                        {displayAlias &&
                                            <Typography data-test="alias-list" variant="caption">
                                                {aliasLabel}
                                            </Typography>
                                        }
                                        <ComponentLinkList
                                            data={result}
                                            includeIsDefinedBy={true}
                                            graph={result.inNamedGraph}
                                            context={sourceComponent}
                                            defaultLang={lang}
                                        />
                                    </div>;
                                }}
                                sortProp="label"
                                errorText={externalDuplicate ?
                                    "This external link already exists or is the source IRI."
                                    :
                                    wrongType ?
                                        "This IRI exists in this project but is of the wrong type."
                                        :
                                        null}
                                isClickableResult={(result) => !result.invalid && !result.deprecated && !exclude.includes(result.id)}
                                title="Enter text to search"
                                onClearResults={this.declineExternal}
                                onNoResults={this.offerExternal}
                                lang={lang}
                                chosen={this.addResource} />
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        {allowMultiple ?
                            <PrimaryButton onClick={this.exitFindResource}>Close</PrimaryButton>
                            :
                            <React.Fragment>
                                <CancelButton onClick={this.exitFindResource} />
                                {permitExternal && <PrimaryAddButton title="Add external link" disabled={!offerExternal} onClick={this.addExternal} />}
                            </React.Fragment>
                        }
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

FindResourceDialog.defaultProps = {
    includeSystemGraph: false,
    allowMultiple: false,
    open: false,
    dataTest: "add-resource",
    sortByLength: true,
    useButton: false,
    buttonTitle: "Find a resource",
    title: "Find Resource",
    permitExternal: false,
    hideButtonOnOpen: false
};

FindResourceDialog.propTypes = {
    classes: PropTypes.object,
    dataTest: PropTypes.string,
    icon: PropTypes.object,
    open: PropTypes.bool,
    excludeByPath: PropTypes.array,
    excludeByPathList: PropTypes.array,
    sortByLength: PropTypes.bool,
    searchClass: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
    ]),
    searchExcludeClass: PropTypes.array,
    components: PropTypes.array,
    useButton: PropTypes.bool,
    exclude: PropTypes.array,
    sourceComponent: PropTypes.string,
    defaultLang: PropTypes.string,
    source: PropTypes.string,
    buttonTitle: PropTypes.string,
    title: PropTypes.string,
    viewLang: PropTypes.string,
    graph: PropTypes.string.isRequired,
    includeSystemGraph: PropTypes.bool,
    hideButtonOnOpen: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    permitExternal: PropTypes.bool,
    permittedExternalType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
    ]),
    onExitFindResource: PropTypes.func,
    onAddResource: PropTypes.func.isRequired
};

export default withStyles(styles)(FindResourceDialog);